import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  ListItemAvatar,
  Menu,
  MenuItem,
  Select,
  Button,
  TextField,
  InputAdornment
  // IconButton
} from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import useStyles from './styles'
import history from 'browserHistory'
import { pestelSsoUrl } from 'utils/pestelSso'
import usePestelAccess from 'utils/pestelAccess'
import { getByIdFromArray, userIsAdmin, translateRole, setFirstInArrayById } from 'helpers/dataHelpers'
import { setActiveWorkspace } from 'utils/auth/actions'
import { getUnreadNotifications, getUnreadRequests } from 'utils/notifications/actions'
import { getNewLegalChangesCount } from 'utils/legalChanges/actions'
import WorkspacePlaceholder from 'static/images/workspace.png'
import AddRoundedIcon from '@material-ui/icons/AddRounded'
import SearchRoundedIcon from '@material-ui/icons/SearchRounded'
import CheckRoundedIcon from '@material-ui/icons/CheckRounded'
import ViewStreamRoundedIcon from '@material-ui/icons/ViewStreamRounded'
import { ReactComponent as MoveLeft } from 'static/icons/move-left.svg'
import { ReactComponent as RequestsIcon } from 'static/icons/requests.svg'
import { ReactComponent as NotificationsIcon } from 'static/icons/notifications.svg'
import { ReactComponent as LawsIcon } from 'static/icons/laws.svg'
import { ReactComponent as DatabaseIcon } from 'static/icons/database.svg'
import { ReactComponent as CalendarIcon } from 'static/icons/calendar.svg'
import { ReactComponent as DocumentsIcon } from 'static/icons/documents.svg'
import { ReactComponent as LegalIcon } from 'static/icons/legal.svg'
import { ReactComponent as ClientsIcon } from 'static/icons/clients.svg'
import AddWorkspaceModal from 'components/AddWorkspaceModal'


const BRAND = 'RegulApp'

const menuItems = [
  { label: 'Legal Updates', path: '/', icon: <NotificationsIcon /> },
  { label: 'Requests', path: '/requests', icon: <RequestsIcon /> },
  { label: 'Advices', path: '/advices', icon: <DatabaseIcon /> },
  { label: 'Action Plan', path: '/action-plan', icon: <CalendarIcon />, disabled: false },
  { label: 'Documents', path: '/documents', icon: <DocumentsIcon /> }
]

const adminMenuItems = [
  { label: 'Legal Updates', path: '/', icon: <NotificationsIcon /> },
  { label: 'Requests', path: '/requests', icon: <RequestsIcon /> },
  { label: 'Advices', path: '/advices', icon: <DatabaseIcon /> },
  { label: 'Laws', path: '/laws', icon: <LawsIcon /> },
  { label: 'Legal Changes', path: '/legal-changes', icon: <LegalIcon /> },
  { label: 'Action Plan', path: '/action-plan', icon: <CalendarIcon />, disabled: false },
  { label: 'Documents', path: '/documents', icon: <DocumentsIcon /> },
  { label: 'Member Groups', path: '/groups', icon: <ClientsIcon /> }
]

const Sidebar = (props) => {
  const {
    classes,
    open,
    handleDrawerClose,
    isDesktop
  } = props
  const localClasses = useStyles()
  const dispatch = useDispatch()
  const workspaces = useSelector(state => state.auth.workspaces)
  const activeWorkspace = useSelector(state => state.auth.activeWorkspace)
  const notificationsUnreadCount = useSelector(state => state.notifications.notificationsUnreadCount)
  const requestsUnreadCount = useSelector(state => state.notifications.requestsUnreadCount)
  const newLegalChangesCount = useSelector(state => state.legalChanges.newLegalChangesCount)
  const name = useSelector(state => state.auth.name)
  const avatar = useSelector(state => state.auth.avatar)
  const email = useSelector(state => state.auth.email)
  const [anchorEl, setAnchorEl] = useState(null)
  const isMobile = useSelector(state => state.common.isMobile)
  const [showAddWorkspace, setShowAddWorkspace] = useState(false)
  const [workspaceQuery, setWorkspaceQuery] = useState('')
  // PESTEL is only offered to people a workspace Owner has granted it to, in
  // Settings -> PESTEL Access. Without a grant the hand-off would be refused,
  // so we do not show the door.
  const { hasAccess: hasPestelAccess } = usePestelAccess()

  useEffect(() => {
    if (activeWorkspace) {
      dispatch(getUnreadNotifications())
      dispatch(getUnreadRequests())
      dispatch(getNewLegalChangesCount())
    }
  }, [activeWorkspace]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const renderMenuIcon = (label, icon) => {
    if (label === 'Legal Updates') {
      return (
        <Badge className={localClasses.badge} color='error' variant='dot' invisible={notificationsUnreadCount <= 0}>
          <ListItemIcon className='sidebar-icon'>
            { icon }
          </ListItemIcon>
        </Badge>
      )
    } else if (label === 'Requests') {
      return (
        <Badge className={localClasses.badge} color='error' variant='dot' invisible={requestsUnreadCount <= 0}>
          <ListItemIcon className='sidebar-icon'>
            { icon }
          </ListItemIcon>
        </Badge>
      )
    } else if (label === 'Legal Changes') {
      return (
        <Badge className={localClasses.badge} color='error' variant='dot' invisible={newLegalChangesCount <= 0}>
          <ListItemIcon className='sidebar-icon'>
            { icon }
          </ListItemIcon>
        </Badge>
      )
    }
    return (
      <ListItemIcon className='sidebar-icon'>
        { icon }
      </ListItemIcon>
    )
  }

  const renderMenuItems = (userTypeLocal) => {
    const targetMenuItems = userIsAdmin(userTypeLocal) ? adminMenuItems : menuItems
    return (
      targetMenuItems.map((menuItem, index) => (
        <ListItem
          key={`sidebar-menu-item-${index}`}
          button
          onClick={() => {
            history.push(menuItem.path)
            if (isMobile) {
              handleDrawerClose()
            }
          }}
          selected={history.location.pathname === menuItem.path}
          className={localClasses.listItem}
          disabled={activeWorkspace ? activeWorkspace.in_invitation && menuItem.label !== 'Legal Updates' : false}
        >
          { renderMenuIcon(menuItem.label, menuItem.icon) }
          <ListItemText
            className={localClasses.pageTitle}
            secondary={menuItem.label}
          />
        </ListItem>
      ))
    )
  }

  const renderWorkspaceMenu = () => {
    const sortedWorkspaces = setFirstInArrayById(workspaces, activeWorkspace ? activeWorkspace.id : null)
    const query = workspaceQuery.trim().toLowerCase()
    return (
      sortedWorkspaces.map((workspace) => {
        const isActive = activeWorkspace && workspace.id === activeWorkspace.id
        // Keep every workspace mounted (so the Select value is never out of range)
        // and hide the ones that don't match the search query.
        const matches = !query || (workspace.name || '').toLowerCase().includes(query)
        return (
          <MenuItem
            key={workspace.id}
            value={workspace.id}
            style={matches ? undefined : { display: 'none' }}
            className={`${localClasses.workspaceMenuItem} ${isActive ? 'is-active' : ''}`}
          >
            <Avatar
              variant='rounded'
              src={workspace && workspace.logo ? workspace.logo : WorkspacePlaceholder}
              className={localClasses.rowAvatar}
            />
            <div className={localClasses.rowText}>
              <span className={localClasses.rowPrimary}>{workspace.name}</span>
              <span className={localClasses.rowSecondary}>{BRAND}</span>
            </div>
            {
              isActive &&
                <CheckRoundedIcon className={localClasses.rowCheck} />
            }
          </MenuItem>
        )
      })
    )
  }

  const handleWorkspaceChange = (event) => {
    dispatch(setActiveWorkspace(getByIdFromArray(workspaces, event.target.value)))
  }

  const handlePestelClick = (event) => {
    event.stopPropagation()
    // Hands the current Regul session over to PESTEL — see utils/pestelSso.
    window.location.assign(pestelSsoUrl())
  }

  const handleShowAddWorkspace = (event) => {
    event.stopPropagation()
    setShowAddWorkspace(true)
  }

  return (
    <Drawer
      className={
        clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })
      }
      variant={isDesktop ? 'permanent' : 'temporary'}
      open={open}
      anchor='left'
      classes={{
        paper: clsx(classes.drawerPaper, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })
      }}
      ModalProps={{
        onClose: () => handleDrawerClose(false)
      }}
    >
      <div>
        <div className={localClasses.mainLogo}>
          <Select
            value={activeWorkspace ? activeWorkspace.id : null}
            onChange={handleWorkspaceChange}
            className={localClasses.workspaceSelect}
            MenuProps={{
              className: localClasses.workspaceSelectPopover,
              autoFocus: false,
              getContentAnchorEl: null,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              TransitionProps: { onExited: () => setWorkspaceQuery('') }
            }}
            inputProps={{
              className: localClasses.workplaceInput
            }}
          >
            <div
              className={localClasses.searchContainer}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <TextField
                autoFocus
                fullWidth
                variant='outlined'
                size='small'
                placeholder='Find a workspace'
                value={workspaceQuery}
                onChange={(e) => setWorkspaceQuery(e.target.value)}
                className={localClasses.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchRoundedIcon className={localClasses.searchIcon} />
                    </InputAdornment>
                  )
                }}
              />
            </div>
            <div className={localClasses.sectionLabel}>{`${BRAND} — Workspaces`}</div>
            {
              renderWorkspaceMenu()
            }
            {
              hasPestelAccess &&
                <div className={localClasses.pestelSection}>
                  <div className={localClasses.sectionLabel}>Pestel</div>
                  <div
                    className={localClasses.pestelItem}
                    onClick={handlePestelClick}
                  >
                    <Avatar variant='rounded' className={localClasses.pestelAvatar}>
                      <ViewStreamRoundedIcon />
                    </Avatar>
                    <div className={localClasses.rowText}>
                      <span className={localClasses.rowPrimary}>PESTEL</span>
                      <span className={localClasses.pestelSecondary}>
                        {activeWorkspace ? activeWorkspace.name : ''}
                      </span>
                    </div>
                  </div>
                </div>
            }
            <div className={localClasses.createWorkspaceContainer}>
              <Button
                startIcon={<AddRoundedIcon />}
                className={localClasses.createWorkspace}
                onClick={handleShowAddWorkspace}
              >
                Create new workspace
              </Button>
            </div>
          </Select>
          {
            open ?
              <MoveLeft
                className={localClasses.moveLeft}
                onClick={handleDrawerClose}
              /> :
              null
          }
        </div>
        <List className={localClasses.listElements}>
          { renderMenuItems(activeWorkspace.role) }
        </List>
      </div>
      {
        showAddWorkspace &&
          <AddWorkspaceModal
            showDialog={showAddWorkspace}
            setShowDialog={setShowAddWorkspace}
            header='New worspace'
          />
      }
      <ListItem
        button
        onClick={handleClick}
        // selected={history.location.pathname === menuItem.path}
        className={`${localClasses.listItem} ${localClasses.account}`}
        // disabled={menuItem.disabled}
      >
        <ListItemAvatar>
          <Avatar
            variant='rounded'
            alt='User avatar'
            src={avatar}
            className={localClasses.avatar}
          />
        </ListItemAvatar>
        <ListItemText
          className={localClasses.pageTitle}
          primaryTypographyProps={{
            variant: 'body2',
            className: localClasses.accountPrimary
          }}
          secondaryTypographyProps={{
            variant: 'caption',
            className: localClasses.accountSecondary
          }}
          primary={name || email}
          secondary={activeWorkspace && activeWorkspace.role ? translateRole(activeWorkspace.role) : '-'}
        />
      </ListItem>
      <Menu
        id='account-menu'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          value='settings'
          onClick={
            () => {
              handleClose()
              history.push('/settings')
            }
          }
        >
          Settings
        </MenuItem>
        <MenuItem
          value='logout'
          onClick={() => history.push('/login')}
        >
          Logout
        </MenuItem>
      </Menu>
    </Drawer>
  )
}

export default Sidebar
